import type en from "../en/overview";
import type { Messages } from "../../types";

const fr: Messages<typeof en> = {
  metaTitle: "Vue d’ensemble",
  welcomeBack: "Bon retour",
  welcomeBackName: "Bon retour, {name}",
  happeningIn: "Voici ce qui se passe dans {store}.",

  // Range picker
  dateRange: "Période",
  rangeToday: "Aujourd’hui",
  range7d: "7 jours",
  range30d: "30 jours",
  range90d: "90 jours",
  salesFor_today: "Ventes du jour",
  salesFor_7d: "Ventes des 7 derniers jours",
  salesFor_30d: "Ventes des 30 derniers jours",
  salesFor_90d: "Ventes des 90 derniers jours",

  // Metrics
  testExcluded: "Commandes en mode test exclues · montants en {currency}",
  paidOrders: "Commandes payées",
  grossSales: "Ventes brutes",
  netSales: "Ventes nettes",
  netSub: "Brut − remises − remboursements",
  aov: "Panier moyen",
  pendingPayment: "Paiements en attente",
  pendingSub: "{count} commandes · pas encore reçu",
  pendingSub_one: "{count} commande · pas encore reçu",
  refunds: "Remboursements",
  def_paidOrders: "Commandes passées sur la période dont le paiement est confirmé (payées, partiellement remboursées, remboursées ou contestées). Les commandes en mode test sont exclues.",
  def_gross: "Somme des sous-totaux des articles avant remises, pour les commandes dont le paiement est confirmé. Hors frais de livraison et montants de taxes seuls. Commandes en mode test exclues.",
  def_net: "Ventes nettes = ventes brutes − remises − remboursements effectués sur la période. Commandes en mode test exclues.",
  def_aov: "Panier moyen = (ventes brutes − remises) ÷ commandes payées.",
  def_pending: "Total des commandes ouvertes encore en attente de paiement (virement bancaire, paiement à la livraison, paiements en ligne inachevés…). Cet argent n’a PAS été reçu.",
  def_refunds: "Remboursements aboutis sur la période, quelle que soit la date de la commande.",
  def_collected: "Montants encaissés par vos prestataires de paiement ou marqués comme reçus pour les paiements hors ligne, avant remboursements. Les frais des prestataires ne sont pas inclus, car ils sont prélevés par votre prestataire.",
  def_customers: "Adresses e-mail de clients distinctes ayant une commande payée sur la période.",
  def_repeat: "Clients (depuis le début) ayant plus d’une commande payée, parmi tous les clients ayant une commande payée.",
  chartTitle: "Ventes nettes de remises par jour",
  chartDesc: "Commandes payées uniquement, par date de commande.",
  chartLabel: "Ventes par jour — {range}",
  fullAnalytics: "Statistiques complètes →",
  noPaidOrdersPeriod: "Aucune commande payée sur cette période pour l’instant.",
  salesHidden: "Les chiffres de ventes sont visibles par les rôles ayant accès aux statistiques.",

  // Recent orders
  recentOrders: "Commandes récentes",
  allOrders: "Toutes les commandes →",
  noOrdersYet: "Aucune commande pour l’instant",
  ordersWillShow: "Les commandes apparaîtront ici dès que des clients passeront commande.",
  publishToReceive: "Publiez votre boutique pour commencer à recevoir des commandes.",
  colOrder: "Commande",
  colCustomer: "Client",
  colPayment: "Paiement",
  colFulfillment: "Expédition",
  testBadge: "Test",
  fin_pending: "En attente",
  fin_awaiting_action: "Action requise",
  fin_authorized: "Autorisé",
  fin_paid: "Payé",
  fin_partially_refunded: "Partiellement remboursé",
  fin_refunded: "Remboursé",
  fin_failed: "Échoué",
  fin_cancelled: "Annulé",
  fin_disputed: "Contesté",
  ful_unfulfilled: "Non expédié",
  ful_partially_fulfilled: "Partiellement expédié",
  ful_fulfilled: "Expédié",
  ful_not_required: "Non requis",

  // Low stock
  lowStock: "Stock faible",
  lowStockDesc: "Produits actifs dont le stock est inférieur ou égal au seuil d’alerte.",
  inventoryLink: "Inventaire →",
  nothingLow: "Aucun produit en stock faible.",
  sku: "SKU {sku}",
  outOfStock: "En rupture de stock",
  leftInStock: "{count} restants",

  // Your store
  yourStore: "Votre boutique",
  draftNotVisible: "Brouillon — invisible pour les clients",
  address: "Adresse",
  alsoAt: "Également : {url}",
  publicationSettings: "Paramètres de publication",

  // Needs attention
  needsAttention: "À traiter",
  noIssues: "Aucun problème de paiement ou d’expédition pour le moment.",
  atCheckout: "Au paiement : {methods}",
  issue_paidUnfulfilled: "{count} commandes payées en attente d’expédition",
  issue_paidUnfulfilled_one: "{count} commande payée en attente d’expédition",
  issue_failed: "{count} paiements ont échoué ces 7 derniers jours",
  issue_failed_one: "{count} paiement a échoué ces 7 derniers jours",
  issue_disputed: "{count} paiements contestés",
  issue_disputed_one: "{count} paiement contesté",
  issue_awaiting: "{count} commandes ouvertes en attente de paiement (pas encore reçu)",
  issue_awaiting_one: "{count} commande ouverte en attente de paiement (pas encore reçu)",
  issue_provider: "{provider} : {state}",
  issue_providerError: "{provider} : {state} — {error}",
  issue_noPayment: "Aucun moyen de paiement n’est activé — les clients ne peuvent pas payer",
  payState_not_connected: "Non connecté",
  payState_setup_required: "Configuration requise",
  payState_connected: "Connecté · non activé",
  payState_test_mode: "Actif au paiement · Mode test",
  payState_active: "Actif au paiement",
  payState_connection_problem: "Problème de connexion",
  payState_reauth_required: "Nouvelle autorisation requise",
  payState_unavailable: "Temporairement indisponible",
  payState_not_supported: "Pas encore pris en charge",

  // Launch checklist
  launchChecklist: "Liste de lancement",
  launchChecklistDesc: "Terminez ces étapes pour commencer à vendre. La progression reflète la configuration réelle de votre boutique.",
  checklistLinksDesc: "Chaque élément renvoie à l’endroit où vous pouvez le compléter.",
  continueSetup: "Poursuivre la configuration",
  blockersCount: "{count} éléments doivent être corrigés avant de pouvoir publier.",
  blockersCount_one: "{count} élément doit être corrigé avant de pouvoir publier.",
  viewRequirements: "Voir les conditions de lancement →",
  stepsDone: "{done} étapes sur {total} terminées",
  launchProgress: "Progression du lancement",
  srDone: "(terminé)",
  srTodo: "(à faire)",
  srOptional: "(facultatif)",
  optionalBadge: "Facultatif",
  doneBadge: "Terminé",
  check_store_created: "Créer votre boutique",
  check_branding: "Ajouter votre logo et votre style de marque",
  check_products: "Ajouter un produit",
  check_payments: "Configurer les paiements",
  check_payments_tested: "Tester vos moyens de paiement",
  check_shipping: "Configurer la livraison",
  check_policies: "Relire les politiques de votre boutique",
  check_domain: "Connecter un domaine personnalisé",
  check_published: "Publier votre boutique",
  checkDetail_products: "Au moins un produit actif avec un prix.",
  checkDetail_payments: "Connectez votre propre compte de paiement ou activez un moyen de paiement hors ligne.",
  blocker_suspended: "Cette boutique a été suspendue par la plateforme. Contactez le support.",
  blocker_contactEmail: "Ajoutez une adresse e-mail de contact dans les paramètres de la boutique.",
  blocker_homePage: "Votre boutique a besoin d’une page d’accueil.",
  blocker_product: "Ajoutez au moins un produit actif.",
  blocker_payment: "Activez au moins un moyen de paiement prêt pour le paiement en ligne.",
  blocker_shipping: "Vous vendez des produits physiques — ajoutez une zone de livraison avec au moins un tarif.",
  warning_policies: "Vos pages de politiques contiennent encore le texte du modèle. Relisez-les avant le lancement.",
  warning_onlineUntested: "Certains moyens de paiement en ligne activés n’ont pas réussi le test de connexion.",
  warning_testMode: "Un moyen de paiement est en mode test — les clients ne peuvent pas effectuer de vrais paiements avec.",

  // Errors / access
  errTitle: "Une erreur s’est produite",
  errBody: "Impossible de charger cette page. Vérifiez votre connexion et réessayez.",
  errForbiddenTitle: "Vous n’avez pas accès à cette page",
  errForbiddenBody: "Votre rôle dans cette boutique ne couvre pas cette section. Demandez l’accès au propriétaire de la boutique.",
  noAccessTitle: "Vous n’avez pas accès à cette section",
  noAccessBody: "Votre rôle dans {store} est {role}, qui ne la couvre pas. Demandez l’accès au propriétaire de la boutique si nécessaire.",
  backToOverview: "Retour à la vue d’ensemble",

  // Onboarding
  setupTitle: "Configurer votre boutique",
  setupDesc: "Une configuration guidée. Votre progression est enregistrée automatiquement — vous pouvez partir et revenir à tout moment.",
  skipForNow: "Passer pour l’instant",
  setupSteps: "Étapes de configuration",
  stepOf: "Étape {step} sur {total}",
  step_branding: "Identité visuelle",
  step_product: "Premier produit",
  step_payments: "Paiements",
  step_shipping: "Livraison",
  step_taxes: "Taxes",
  step_policies: "Politiques",
  step_domain: "Domaine",
  step_preview: "Aperçu",
  step_publish: "Publication",
  skipStep: "Passer cette étape",
  goToOverview: "Aller à la vue d’ensemble",
  logoTitle: "Ajouter votre logo",
  logoIntro: "Votre logo apparaît dans l’en-tête de la boutique, dans les e-mails et dans l’onglet du navigateur.",
  logoLabel: "Logo",
  logoHint: "Une image PNG ou de type SVG avec un fond transparent donne le meilleur résultat.",
  saveLogo: "Enregistrer le logo",
  logoSaved: "Logo enregistré.",
  errUploadLogo: "Importez d’abord un logo.",
  colorsInBuilder: "Les couleurs, polices et la mise en page se règlent dans le",
  colorsInBuilderLink: "constructeur de boutique",
  productTitle: "Ajouter votre premier produit",
  productIntro: "Commencez par un produit — vous pourrez ajouter des variantes, d’autres images et le référencement plus tard dans l’éditeur complet.",
  productAlready: "Vous avez déjà un produit actif. Vous pouvez en ajouter un autre ici ou continuer.",
  productName: "Nom du produit",
  productNamePh: "ex. Mug en céramique fait main",
  stock: "Stock",
  stockHint: "Laissez vide pour ne pas suivre le stock.",
  typePhysical: "Physique (expédié)",
  typeService: "Service (sans livraison)",
  imageOptional: "Image (facultatif)",
  addProduct: "Ajouter le produit",
  fullProductEditor: "Utiliser l’éditeur de produit complet (produits numériques, variantes) →",
  errEnterPrice: "Saisissez un prix.",
  productActive: "« {title} » est actif dans votre catalogue.",
  productFallback: "Produit",
  paymentsTitle: "Choisir comment vos clients paient",
  paymentsIntro: "Les paiements arrivent directement sur vos propres comptes. Vous connectez votre propre compte Stripe ou PayPal, ou acceptez le paiement à la livraison / le virement bancaire — la plateforme n’encaisse jamais de paiements à votre place et il n’existe aucun compte de secours.",
  customersCanPay: "Vos clients peuvent payer avec :",
  cantCheckout: "Les clients ne peuvent pas finaliser leur commande tant qu’aucun moyen de paiement n’est activé.",
  connectStripePaypal: "Connecter Stripe ou PayPal",
  cod: "Paiement à la livraison",
  codHint: "Les clients paient à la livraison ; vous marquez les commandes comme payées à réception de l’argent.",
  enabledInCheckout: "Activé au paiement",
  enableCod: "Activer le paiement à la livraison",
  bankTransfer: "Virement bancaire",
  bankDetails: "Coordonnées bancaires affichées aux clients",
  bankDetailsHint: "Titulaire du compte, IBAN / numéro de compte, banque et référence de paiement à utiliser.",
  enableBank: "Activer le virement bancaire",
  askOwnerPayments: "Demandez au propriétaire de la boutique ou à un administrateur de configurer les paiements.",
  shippingTitle: "Configurer la livraison",
  shippingIntro: "Ajoutez un tarif forfaitaire pour les commandes livrées en {country}. Vous pourrez ajouter d’autres zones et types de tarifs plus tard.",
  shippingNotRequired: "Vous n’avez actuellement aucun produit physique actif, la livraison n’est donc pas obligatoire. Configurez-la si vous vendez des produits physiques.",
  shippingHasRate: "Vous avez au moins un tarif de livraison actif.",
  rateName: "Nom du tarif",
  standardShipping: "Livraison standard",
  freeShipping: "Livraison gratuite",
  priceZeroHint: "Indiquez 0 pour la livraison gratuite.",
  freeOver: "Livraison gratuite dès",
  noFreeShipping: "Pas de livraison gratuite",
  deliveryEstimate: "Délai de livraison estimé",
  deliveryEstimatePh: "ex. 2 à 4 jours ouvrés",
  saveShippingRate: "Enregistrer le tarif",
  allShippingSettings: "Tous les paramètres de livraison →",
  shippingSetUp: "La livraison vers {country} est configurée.",
  taxesIntro: "Indiquez si vos prix incluent les taxes et ajoutez les taux que vous devez appliquer.",
  taxResponsibleTitle: "Vous êtes responsable des taux de taxe",
  taxResponsibleBody: "Storelancer applique les taux que vous saisissez ; il ne détermine pas quelles taxes s’appliquent à votre activité et ne calcule pas automatiquement les taxes selon la juridiction. Renseignez-vous auprès d’un conseiller fiscal.",
  openTaxSettings: "Ouvrir les paramètres de taxes",
  markReviewed: "Marquer comme vérifié",
  policiesTitle: "Relire vos politiques",
  policiesIntro: "Votre boutique a été créée avec des pages modèles pour les remboursements, la livraison, la confidentialité et les conditions. Adaptez-les à votre fonctionnement réel avant le lancement.",
  editPages: "Modifier les pages",
  policiesReviewed: "J’ai relu mes politiques",
  domainTitle: "Connecter un domaine (facultatif)",
  domainIntro: "Votre boutique dispose déjà d’une adresse gratuite sur la plateforme. Vous pouvez connecter un domaine qui vous appartient à tout moment.",
  connectDomain: "Connecter un domaine",
  skipFreeAddress: "Passer — utiliser l’adresse gratuite",
  previewTitle: "Prévisualiser votre boutique",
  previewIntro: "Voyez votre boutique exactement comme vos clients la verront, avant tout le monde.",
  openPreview: "Ouvrir l’aperçu ↗",
  looksGood: "C’est bon — continuer",
  publishIntro: "Quand tout est prêt, publiez votre boutique depuis la page de lancement. Rien n’est publié automatiquement.",
  stillRequired: "Encore requis avant publication",
  storeIsLive: "Votre boutique est en ligne.",
  everythingInPlace: "Tout ce qui est requis est en place.",
  goToPublish: "Aller au lancement et à la publication",
  errUnknownStep: "Étape inconnue",

  // Launch & publish
  publishDesc: "Vérifiez ce qu’il reste à faire avant que les clients puissent visiter votre boutique et y acheter.",
  thingsToFix: "{count} points à corriger avant de publier",
  thingsToFix_one: "{count} point à corriger avant de publier",
  readyToPublish: "Prête à être publiée",
  allRequirementsMet: "Toutes les conditions de lancement sont remplies.",
  allStepsComplete: "Toutes les étapes obligatoires sont terminées.",
  recommendedBeforeLaunch: "Recommandé avant le lancement",
  publication: "Publication",
  firstPublished: "Publiée pour la première fois le {date}.",
  onlyOwnerPublish: "Seul le propriétaire de la boutique ou un administrateur peut publier ou dépublier cette boutique.",
  suspendedCantPublish: "Cette boutique est suspendue par la plateforme et ne peut pas être publiée.",
  unpublishConfirmTitle: "Dépublier votre boutique ?",
  unpublishConfirmBody: "Les clients ne pourront plus parcourir la boutique ni acheter. Les commandes, produits et paramètres sont conservés, et vous pouvez republier à tout moment. Les paiements déjà en cours peuvent encore être finalisés par le prestataire de paiement.",
  unpublishStore: "Dépublier la boutique",
  publishStore: "Publier la boutique",
  fixRedFirst: "Corrigez d’abord les éléments indiqués en rouge. Votre boutique sera revérifiée au moment de la publication.",
  publishConfirmTitle: "Publier votre boutique ?",
  publishConfirmBody: "Votre boutique devient visible par tous et les clients peuvent passer commande avec les moyens de paiement que vous avez activés.",
  publishNow: "Publier maintenant",
  previewStore: "Prévisualiser la boutique ↗",
  publicAddresses: "Adresses publiques",
  platformAddress: "Adresse sur la plateforme",
  primaryDomain: "Domaine principal (vérifié)",
  connectDomainIn: "Connectez un domaine personnalisé dans",
  addressesNotAvailable: "Ces adresses affichent une page « indisponible » tant que vous n’avez pas publié.",
  storeOffline: "Votre boutique est hors ligne. Les clients voient désormais une page « indisponible ».",
};

export default fr;
